import * as React from 'react';
import propz from 'propz';
import { SchoolEvent, SchoolEventTeamDataPlayer } from 'Src/models/event';
import { SchoolEventTeamPlayerPosition } from './SchoolEventTeamPlayerPosition';
import { SchoolEventTeamPlayerSubOrCaptain } from './SchoolEventTeamPlayerSubOrCaptain';
import { SchoolEventPlayerSummary } from 'Src/components/SchoolEventPlayersSummary/SchoolEventPlayersSummary';
import {
  isClubEvent,
  isEventStatusFinished,
  isTeamSportEvent,
  isMultipartyEvent,
  isShowIndividualScoreForTeam
} from '../../../../../../helpers/event/event';
import {
  convertPointsToStringWithoutDelimeter,
  getIndividualScoreByStudent
} from '../../../../../../helpers/event/schoolEventViewHelper';
import './SchoolEventTeamPlayers.scss';
import { convertPointsCricket, isCricket } from '../../../../../../helpers/sport/cricket';
import { Report } from 'Src/models/report';
import moment from 'moment';
import { AvailabilityStatusDot } from 'Src/components/AvailabilityStatusDot/AvailabilityStatusDot';

interface Props {
  players: SchoolEventTeamDataPlayer[];
  event: SchoolEvent;
  playersForSummary?: SchoolEventPlayerSummary[];
  onChangeIsCaptainPlayer?: (event, playerId: string) => void;
  onChangeIsViceCaptainPlayer?: (event, playerId: string) => void;
  onChangeIsSubPlayer?: (event, playerId: string) => void;
  availabilityReports?: Report[];
  availabilitySelfService?: boolean;
}

export function SchoolEventTeamPlayers(props: Props) {
  const showAvailability = props.availabilityReports !== undefined;
  const {
    players,
    event,
    playersForSummary = [],
    onChangeIsCaptainPlayer,
    onChangeIsViceCaptainPlayer,
    onChangeIsSubPlayer,
    availabilityReports = []
  } = props;

  const isShowIndividualScore = isShowIndividualScoreForTeam(event, players);
  const isShowRoleToggles = !isClubEvent(event) && isTeamSportEvent(event);
  const availabilityByPermission = availabilityReports.reduce((result, report) => {
    const key = String(report.playerDetails?.permissionId || '');
    const previous = result.get(key);
    if (key && (!previous || moment(report.createdAt).isAfter(moment(previous.createdAt)))) result.set(key, report);
    return result;
  }, new Map<string, Report>());

  const getPlayerSummary = (player: SchoolEventTeamDataPlayer) => {
    return playersForSummary.find(playerForSummary => {
      if (playerForSummary.playerId === player.id) {
        return true;
      }

      if (
        playerForSummary.userId === player.userId &&
        playerForSummary.permissionId === player.permissionId &&
        playerForSummary.teamId === player.teamId
      ) {
        return true;
      }

      return (
        playerForSummary.teamId === player.teamId &&
        Boolean(playerForSummary.playerId) &&
        playerForSummary.name === `${player.firstName} ${player.lastName}`
      );
    });
  };

  const getPlayerState = (player: SchoolEventTeamDataPlayer) => {
    const playerSummary = getPlayerSummary(player);

    if (playerSummary) {
      return {
        isCaptain: Boolean(playerSummary.isCaptain),
        isViceCaptain: Boolean(playerSummary.isViceCaptain),
        isSub: Boolean(playerSummary.isSub),
        playerSummary
      };
    }

    const playerData = event.players.find(eventPlayer => eventPlayer._id === player.id);

    return {
      isCaptain: Boolean(playerData && playerData.isCaptain),
      isViceCaptain: Boolean(playerData && playerData.isViceCaptain),
      isSub: Boolean(playerData && playerData.sub),
      playerSummary: undefined
    };
  };

  const sortedPlayers = players
    .map((player, playerIndex) => ({
      player,
      playerIndex,
      isSub: getPlayerState(player).isSub
    }))
    .sort((a, b) => {
      if (a.isSub !== b.isSub) {
        return a.isSub ? 1 : -1;
      }

      return a.playerIndex - b.playerIndex;
    });

  const getPlayerAvatarUrl = (player: SchoolEventTeamDataPlayer) => {
    const imageSrc =
      propz.get(player, ['profile', 'avatar'], '') ||
      propz.get(player, ['user', 'profile', 'avatar'], '') ||
      propz.get(player, ['userProfile', 'avatar'], '') ||
      propz.get(player, ['profileData', 'avatar'], '') ||
      propz.get(player, ['avatar'], '') ||
      propz.get(player, ['avatarUrl'], '') ||
      propz.get(player, ['photoUrl'], '') ||
      propz.get(player, ['pic'], '') ||
      propz.get(player, ['photo'], '') ||
      propz.get(player, ['picture'], '') ||
      propz.get(player, ['image'], '') ||
      propz.get(player, ['imageUrl'], '');

    return imageSrc && String(imageSrc).startsWith('//') ? `https:${imageSrc}` : imageSrc;
  };

  const getPlayerInitials = (player: SchoolEventTeamDataPlayer) => {
    return [player.firstName, player.lastName]
      .filter(Boolean)
      .map(namePart => namePart.charAt(0))
      .join('')
      .toUpperCase();
  };

  const renderToggle = (
    label: string,
    role: 'captain' | 'substitute',
    isActive: boolean,
    onClick: (() => void) | undefined,
    ariaLabel: string
  ) => (
    <button
      type="button"
      className={`ePlayerRoleToggle ePlayerRoleToggle--${role}${isActive ? ' ePlayerRoleToggle--active' : ''}`}
      onClick={onClick}
      disabled={!onClick}
      aria-label={ariaLabel}
      aria-pressed={isActive}
    >
      {label}
    </button>
  );

  const renderPlayerRow = (player: SchoolEventTeamDataPlayer, playerIndex: number) => {
    const { userId, permissionId } = player;
    const isEventFinished = isEventStatusFinished(event);
    const playerPoints = getIndividualScoreByStudent(event, userId, permissionId);
    const { isCaptain, isViceCaptain, isSub, playerSummary } = getPlayerState(player);
    const editablePlayerId = playerSummary ? playerSummary.playerId : player.id;
    const avatarUrl = getPlayerAvatarUrl(player);
    const availability = availabilityByPermission.get(String(permissionId));

    let playerPointsStr;

    if (isCricket(event) && !isMultipartyEvent(event)) {
      const { runs, wickets } = convertPointsCricket(playerPoints);
      playerPointsStr = `${runs} / ${wickets}`;
    } else {
      playerPointsStr = convertPointsToStringWithoutDelimeter(event, playerPoints);
    }

    const isShowPoints = (isEventFinished && isShowIndividualScore) || Boolean(playerPoints);

    return (
      <tr key={`school_players_${player.id}`}>
        <td style={{ width: '5%' }}>
          <span className={'ml-1'}>{playerIndex + 1}</span>
        </td>

        <td style={{ width: '30%' }} className="eSchoolPlayerNamePart" colSpan={2}>
          <div className="eSchoolPlayerIdentity">
            <span className="eSchoolPlayerAvatar">
              {avatarUrl ? (
                <img className="eSchoolPlayerAvatar__img" src={avatarUrl} alt="" />
              ) : (
                <span className="eSchoolPlayerAvatar__fallback">{getPlayerInitials(player)}</span>
              )}
            </span>
            <span className="eSchoolPlayerName">
              {player.firstName} {player.lastName}
            </span>
            {showAvailability ? <AvailabilityStatusDot report={availability} selfService={props.availabilitySelfService} /> : null}
          </div>
        </td>

        <td style={{ width: '22%' }}>
          {isShowPoints && <span className={'ml-3 font-weight-bold'}>{`${playerPointsStr} `}</span>}
        </td>

        <td style={{ width: '30%' }}>
          <div className={'ePlayersPosition'}>
            <SchoolEventTeamPlayerPosition player={player} event={event} />
          </div>
        </td>

        <td style={{ width: '13%' }}>
          <div className={'ePlayersSubOrCaptain'}>
            {isShowRoleToggles ? (
              <div className="ePlayerRoleToggles">
                {renderToggle(
                  'C',
                  'captain',
                  isCaptain,
                  onChangeIsCaptainPlayer
                    ? () =>
                        onChangeIsCaptainPlayer(
                          {
                            target: {
                              checked: !isCaptain
                            }
                          },
                          editablePlayerId
                        )
                    : undefined,
                  isCaptain ? 'Remove captain' : 'Mark as captain'
                )}
                {renderToggle(
                  'VC',
                  'captain',
                  isViceCaptain,
                  onChangeIsViceCaptainPlayer
                    ? () =>
                        onChangeIsViceCaptainPlayer(
                          {
                            target: {
                              checked: !isViceCaptain
                            }
                          },
                          editablePlayerId
                        )
                    : undefined,
                  isViceCaptain ? 'Remove vice captain' : 'Mark as vice captain'
                )}
                {renderToggle(
                  'S',
                  'substitute',
                  isSub,
                  onChangeIsSubPlayer
                    ? () =>
                        onChangeIsSubPlayer(
                          {
                            target: {
                              checked: !isSub
                            }
                          },
                          editablePlayerId
                        )
                    : undefined,
                  isSub ? 'Remove substitute' : 'Mark as substitute'
                )}
              </div>
            ) : (
              <SchoolEventTeamPlayerSubOrCaptain player={player} event={event} />
            )}
          </div>
        </td>
      </tr>
    );
  };

  const starterRows = sortedPlayers.filter(({ isSub }) => !isSub);
  const substituteRows = sortedPlayers.filter(({ isSub }) => isSub);

  const rows = [
    ...starterRows.map(({ player }, playerIndex) => renderPlayerRow(player, playerIndex)),
    ...(substituteRows.length > 0
      ? [
          <tr key="school_players_substitutes_heading" className="eSchoolPlayerGroupRow">
            <td colSpan={6}>
              <span>Substitutes</span>
            </td>
          </tr>,
          ...substituteRows.map(({ player }, playerIndex) => renderPlayerRow(player, starterRows.length + playerIndex))
        ]
      : [])
  ];

  return (
    <table className={'w-100'}>
      <tbody>{rows}</tbody>
    </table>
  );
}
